import { Check } from 'lucide-react';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PROJECT_PALETTE, onColor } from '@tt/shared';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Swatch } from '@/components/ui/swatch';
import { cn } from '@/lib/utils';

interface ColorPickerProps {
  value: string;
  onChange: (color: string) => void;
  /** Names what is being coloured, for the trigger's accessible name. */
  label: string;
  disabled?: boolean;
}

/**
 * Swatch that opens the project palette: a vivid and a pastel tone of each hue.
 *
 * Every swatch is a real radio, so arrow keys move between them and the current colour is
 * announced as checked. Colour is never the only cue — the selected swatch carries a tick, and
 * each one's accessible name is its hue and tone.
 */
export function ColorPicker({ value, onChange, label, disabled }: ColorPickerProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const name = useId();
  const selected = value.toUpperCase();

  const rows = [
    { tone: 'vivid' as const, title: t('color.vivid') },
    { tone: 'pastel' as const, title: t('color.pastel') },
  ];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        disabled={disabled}
        aria-label={t('color.change', { name: label })}
        className="inline-flex h-8 w-8 items-center justify-center rounded-md border bg-background disabled:opacity-50"
      >
        <Swatch color={value} className="h-4 w-4" />
      </PopoverTrigger>
      <PopoverContent className="w-auto p-3">
        <div role="radiogroup" aria-label={t('color.palette')} className="space-y-3">
          {rows.map((row) => (
            <div key={row.tone}>
              <p className="mb-1.5 text-xs font-medium text-muted-foreground">{row.title}</p>
              <div className="flex gap-1.5">
                {PROJECT_PALETTE.map((entry) => {
                  const color = entry[row.tone];
                  const isSelected = color.toUpperCase() === selected;
                  return (
                    <label
                      key={color}
                      className={cn(
                        'relative inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-md',
                        'focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-1',
                      )}
                      title={`${t(`color.hue.${entry.name}`)} · ${row.title}`}
                    >
                      <input
                        type="radio"
                        name={name}
                        value={color}
                        checked={isSelected}
                        // `change` does not fire when the current colour is clicked again, so
                        // closing is bound to the click: picking what is already set should
                        // still dismiss the palette, and should not write anything.
                        onChange={() => onChange(color)}
                        onClick={() => setOpen(false)}
                        // Covers the swatch rather than hiding in a corner of it, so the
                        // radio itself is what a pointer hits.
                        className="absolute inset-0 cursor-pointer opacity-0"
                        aria-label={`${t(`color.hue.${entry.name}`)} ${row.title}`}
                      />
                      <Swatch color={color} className="h-7 w-7" />
                      {isSelected ? (
                        <Check
                          className="pointer-events-none absolute h-4 w-4"
                          style={{ color: onColor(color) }}
                          aria-hidden="true"
                        />
                      ) : null}
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
